<template>
  <div>
    <div class="d-flex justify-space-between mb-4">
      <tutorial-tooltip
        title="Stay Connected with Suppliers"
        value="seen_supplier_chat"
      >
        <div class="mt-4 qtm-h4 text-secondary">
          Supplier Chat
        </div>
        <template v-slot:text>
          Chat with suppliers for quick updates and faster decision-making.
          You'll be notified as soon as a supplier responds.
        </template>
      </tutorial-tooltip>
      <sort-menu-order
        v-if="messages.length"
        v-model="sortBy"
      />
    </div>
    <div v-if="!messages.length && !hideEmpty" class="bg-background pa-4">
      <div class="qtm-body text-mid-grey">
        Chat with your supplier about the {{ type }}.
      </div>
      <qtm-btn class="ml-n4" :loading="loading" prepend-icon="mdi-message-outline" tertiary @click="hideEmpty = true">
        Message Your Supplier
      </qtm-btn>
    </div>
    <div
      v-else
      class="d-flex flex-column bg-background pa-4 messages-wrapper"
      :class="{ 'flex-column-reverse': sortBy === 'oldest' }"
    >
      <qtm-input-label
        class="pb-6 position-relative"
        color="dark-grey"
        @dragleave="fileDropperExpanded = false"
        @dragover="fileDropperExpanded = true"
        @drop="fileDropperExpanded = false"
      >
        <template v-slot:label>
          <div class="mb-2">
            {{ inputLabel.text }}
            <out-of-office-status v-if="inputLabel.outOfOffice" class="ml-2" />
          </div>
        </template>
        <qtm-textarea
          v-model="message"
          v-model:focused="focused"
          auto-grow
          :autofocus="hideEmpty"
          hide-details
          placeholder="Type your message"
          :rows="expanded ? 3 : 1"
          @paste="onPaste"
        />
        <order-attachments
          v-if="attachments.length || fileInputList.length"
          v-model="attachments"
          :uploading-files="fileInputList"
          dense
          no-upload
          class="bg-transparent"
          remove-title
        />
        <div class="d-flex mt-2">
          <file-dropper
            v-model="fileInputList"
            icon-only
            multiple
            :expanded="debouncedExpanded"
            @change="uploadFile"
          />
          <v-spacer />
          <template v-if="expanded">
            <qtm-btn :loading="loading" tertiary @click="cancel">
              Cancel
            </qtm-btn>
            <qtm-btn :disabled="disabled" :loading="loading" @click="save">
              Send to Supplier
            </qtm-btn>
          </template>
        </div>
      </qtm-input-label>
      <supplier-chat-message
        v-for="msg in sortedMessages"
        :key="msg.id"
        class="py-4"
        :mark-as-read="markAsRead"
        :message="msg"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { refDebounced } from '@vueuse/core'
import type { Attachment, Order, Quote } from '@quotetome/materials-api'
import OutOfOfficeStatus from '@/components/vendors/out-of-office-status.vue'
import OrderAttachments from '@/components/orders/order-attachments.vue'
import SupplierChatMessage from '@/components/vendors/supplier-chat-message.vue'
import SortMenuOrder from '@/components/sort-menu-order.vue'
import TutorialTooltip from '@/components/tutorial-tooltip.vue'

export interface Props {
  order: Order
  quote: Quote
  type?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: 'quote'
})

const authStore = useAuthStore()
const user = authStore.user!
const userName = `${user.first_name} ${user.last_name}`

const attachments = ref<Attachment[]>([])
const fileDropperExpanded = ref(false)
const debouncedExpanded = refDebounced(fileDropperExpanded, 100)
const fileInputList = ref<File[]>([])
const focused = ref(false)
const loading = ref(false)
const message = ref('')
const messages = ref<any[]>([])
const hideEmpty = ref(false)
const sortBy = ref('newest')

const contact = computed(() => props.quote.salesperson)
const disabled = computed(() => !message.value || fileInputList.value.length > 0)
const expanded = computed(() => focused.value || message.value || attachments.value.length > 0)
const inputLabel = computed(() => {
  const base = { text: userName, outOfOffice: false }

  if (contact.value && expanded.value) {
    base.text = `${userName} to ${contact.value.first_name} ${contact.value.last_name}`
    base.outOfOffice = !!contact.value.ooo_date_started
  }

  return base
})
const markAsRead = computed(() => user.id === props.order.owner!.id)
const sortedMessages = computed(() => {
  const toSort = messages.value

  toSort.sort((a, b) => b.date_created - a.date_created)

  return toSort
})

const { $api, $error, $toast } = useNuxtApp()

watch(() => props.quote, async () => {
  messages.value = []

  loading.value = true
  try {
    messages.value = await $api.v1.quotes.chat.listAll(props.quote.id!)
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}, { immediate: true })

const cancel = () => {
  message.value = ''
  attachments.value.forEach(attachment => $api.v1.attachments.delete(attachment.id))
  attachments.value = []
}

const save = async () => {
  loading.value = true
  try {
    const newMessage = await $api.v1.quotes.chat.create(props.quote.id!, {
      attachments: attachments.value.map(attachment => attachment.id),
      text: message.value,
    })

    messages.value.push(newMessage)
    $toast.success('Message sent')
    attachments.value = []
    message.value = ''
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}

const onPaste = async (e: any) => {
  if (e.clipboardData.files.length) {
    e.preventDefault()
    fileInputList.value.push(...Array.from(e.clipboardData.files) as File[])
    await uploadFile()
  }
}

const uploadFile = () => {
  fileInputList.value.forEach(f => {
    $api.v1.attachments.directUpload(f).then(response => {
      attachments.value.push(response)
    }).catch($error.report).finally(() => {
      fileInputList.value = fileInputList.value.filter(item => item !== f)
    })
  })
}
</script>

<style lang="scss" scoped>
$border: 1px solid rgb(var(--v-theme-light-grey));

.messages-wrapper>*:not(:last-child) {
  border-bottom: $border;
}

.messages-wrapper.flex-column-reverse>*:not(:last-child) {
  border-top: $border;
  border-bottom: none;
}
</style>
